import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, API, CONFIG, formatDate, formatDateTime } from "@/lib/api";
import { StaffEditDialog } from "@/components/StaffEditDialog";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, MapPin, CalendarDays, Users, CalendarCheck, Settings2, Plus, Trash2, Download, Printer, CheckCircle2, XCircle, ShieldCheck, ShieldAlert, ChevronLeft, ChevronRight, CalendarRange, Camera, Wifi, UserCheck, Plane, UserX, AlertTriangle, ScanFace, Trophy, Coffee, Moon } from "lucide-react";

const getPos = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

const t = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");
const mins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const ago = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
const LEAVE_CLS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800" };

export default function Attendance() {
  const { hasPerm, user } = useAuth();
  const manage = hasPerm("attendance");
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-4" data-testid="attendance-page">
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">Workforce</div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2"><Clock className="w-6 h-6" /> Attendance</h1>
        <p className="text-sm text-muted-foreground">Clock in/out with GPS verification, manage leave and monthly timetables.</p>
      </div>
      {manage && <AttendanceDashboard />}
      <Tabs defaultValue="clock">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="clock" data-testid="att-tab-clock">Clock In/Out</TabsTrigger>
          <TabsTrigger value="mine" data-testid="att-tab-mine">My Records</TabsTrigger>
          <TabsTrigger value="leave" data-testid="att-tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="att-tab-shifts">My Roster</TabsTrigger>
          <TabsTrigger value="fasting-mine" data-testid="att-tab-fasting-mine">Fasting</TabsTrigger>
          {isAdmin && <TabsTrigger value="team" data-testid="att-tab-team">Team Records</TabsTrigger>}
          {isAdmin && <TabsTrigger value="week" data-testid="att-tab-week">Team Week</TabsTrigger>}
          {isAdmin && <TabsTrigger value="staff" data-testid="att-tab-staff">Staff Admin</TabsTrigger>}
          {isAdmin && <TabsTrigger value="roster" data-testid="att-tab-roster">Timetable</TabsTrigger>}
          {isAdmin && <TabsTrigger value="settings" data-testid="att-tab-settings">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="clock"><ClockPanel /></TabsContent>
        <TabsContent value="mine"><MyRecords /></TabsContent>
        <TabsContent value="leave"><LeavePanel manage={manage} /></TabsContent>
        <TabsContent value="shifts"><MyShifts /></TabsContent>
        <TabsContent value="fasting-mine"><MyFasting /></TabsContent>
        {isAdmin && <TabsContent value="team"><TeamRecords /></TabsContent>}
        {isAdmin && <TabsContent value="week"><TeamWeek /></TabsContent>}
        {isAdmin && <TabsContent value="staff"><StaffAttendance /></TabsContent>}
        {isAdmin && <TabsContent value="roster"><RosterHolidays /></TabsContent>}
        {isAdmin && <TabsContent value="settings"><AttSettings /></TabsContent>}
      </Tabs>
    </div>
  );
}

function ClockPanel() {
  const [data, setData] = useState({ record: null, settings: {} });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState("idle");
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [selfieOpen, setSelfieOpen] = useState(false);
  const pendingType = useRef(null);
  const load = useCallback(() => api.get("/attendance/today").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 15000);

  const rec = data.record;
  const open = rec?.open;
  const onBreak = rec?.on_break;
  const requireGeo = data.settings?.require_geofence;
  const requireFace = data.settings?.require_face_match;
  const requireSelfie = data.settings?.require_selfie || requireFace;
  const requireWifi = data.settings?.require_wifi;
  const [enrolled, setEnrolled] = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const loadFace = useCallback(() => api.get("/attendance/face/status").then((r) => setEnrolled(r.data.enrolled)).catch(() => {}), []);
  useEffect(() => { loadFace(); }, [loadFace]);

  const doClock = async (type, selfie) => {
    setBusy(true); setGps("locating");
    const pos = await getPos();
    setGps(pos ? "ok" : "fail");
    if (requireGeo && !pos) { toast.error("Location required — please allow GPS access in your browser"); setBusy(false); return; }
    try {
      await api.post("/attendance/clock", { type, ...(pos || {}), selfie, note });
      toast.success(type === "in" ? "Clocked in" : "Clocked out");
      setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doBreak = async (type) => {
    setBusy(true);
    try { await api.post("/attendance/clock", { type }); toast.success(type === "break_start" ? "Break started" : "Break ended"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const startClock = (type) => {
    if (requireFace && !enrolled) { toast.error("Please enroll your face first"); setEnrollOpen(true); return; }
    if (requireSelfie) { pendingType.current = type; setSelfieOpen(true); }
    else doClock(type, null);
  };
  const onCaptured = (dataUrl) => { setSelfieOpen(false); doClock(pendingType.current, dataUrl); };
  const doEnroll = async (dataUrl) => { setEnrollOpen(false); try { await api.post("/attendance/face/enroll", { selfie: dataUrl }); toast.success("Face enrolled successfully"); setEnrolled(true); loadFace(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <SelfieDialog open={selfieOpen} onClose={() => setSelfieOpen(false)} onCapture={onCaptured} />
      <SelfieDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} onCapture={doEnroll} />
      <Card className="p-6 space-y-4" data-testid="att-clock-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-mono text-muted-foreground uppercase">{formatDate(todayStr())}</div>
            <div className="text-lg font-semibold">{onBreak ? "You are on BREAK" : open ? "You are clocked IN" : rec ? "Clocked out" : "Not clocked in"}</div>
          </div>
          <Badge className={onBreak ? "bg-amber-500" : open ? "bg-emerald-600" : "bg-slate-500"} data-testid="att-status-badge">{onBreak ? "ON BREAK" : open ? "ON DUTY" : "OFF"}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{requireGeo ? <span className="text-amber-700 font-medium">GPS geo-fence enforced</span> : "GPS recorded"}</span>
          {requireWifi && <span className="flex items-center gap-1 text-amber-700 font-medium"><Wifi className="w-4 h-4" /> Company WiFi only</span>}
          {requireSelfie && <span className="flex items-center gap-1 text-amber-700 font-medium"><Camera className="w-4 h-4" /> Selfie required</span>}
          {requireFace && <span className="flex items-center gap-1 text-amber-700 font-medium"><ScanFace className="w-4 h-4" /> AI face match</span>}
          {gps === "locating" && <span>• locating…</span>}
          {gps === "ok" && <span className="text-emerald-700">• location captured</span>}
          {gps === "fail" && <span className="text-rose-700">• GPS unavailable</span>}
        </div>
        <Textarea placeholder="Optional note (e.g. site visit, WFH)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="att-note" rows={2} />
        {requireFace && (
          <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${enrolled ? "border-emerald-200" : "border-amber-300 bg-amber-50 dark:bg-amber-950/20"}`} data-testid="face-enroll-row">
            <span className="flex items-center gap-1"><ScanFace className="w-4 h-4" /> {enrolled ? "Face enrolled — AI match active" : "Face not enrolled — enroll to clock in"}</span>
            <Button size="sm" variant="outline" onClick={() => setEnrollOpen(true)} data-testid="face-enroll-btn">{enrolled ? "Re-enroll" : "Enroll face"}</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button disabled={busy || open || onBreak} onClick={() => startClock("in")} className="bg-emerald-600 hover:bg-emerald-700 h-12" data-testid="att-clock-in"><LogIn className="w-4 h-4 mr-1" /> Clock In</Button>
          <Button disabled={busy || !open} onClick={() => startClock("out")} variant="outline" className="border-rose-300 text-rose-700 h-12" data-testid="att-clock-out"><LogOut className="w-4 h-4 mr-1" /> Clock Out</Button>
          <Button disabled={busy || !open} onClick={() => doBreak("break_start")} variant="outline" className="border-amber-300 text-amber-700 h-11" data-testid="att-break-start"><Coffee className="w-4 h-4 mr-1" /> Start Break</Button>
          <Button disabled={busy || !onBreak} onClick={() => doBreak("break_end")} variant="outline" className="border-cyan-300 text-cyan-700 h-11" data-testid="att-break-end"><Coffee className="w-4 h-4 mr-1" /> End Break</Button>
        </div>
      </Card>
      <Card className="p-6" data-testid="att-today-card">
        <div className="text-sm font-semibold mb-3 flex items-center justify-between">
          Today's Sessions {rec && <span className="text-xs text-muted-foreground">Total {mins(rec.work_minutes)}{rec.late && <Badge className="ml-2 bg-amber-500">LATE</Badge>}</span>}
        </div>
        {rec?.sessions?.length ? (
          <div className="space-y-2">
            {rec.sessions.map((s, i) => (
              <div key={s.in?.at || i} className="flex items-center justify-between text-sm border rounded-md px-3 py-2" data-testid={`att-session-${i}`}>
                <span className="flex items-center gap-2">
                  {s.in?.selfie_file_id && isAdmin && <img src={`${API}/files/${s.in.selfie_file_id}/download`} alt="in" className="w-8 h-8 rounded object-cover" />}
                  <span className="font-mono">{t(s.in?.at)} → {t(s.out?.at)}</span>
                  {s.in?.break_resume && <Badge variant="outline" className="text-[10px] border-cyan-300 text-cyan-700">after break</Badge>}
                  {s.out?.break && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">break</Badge>}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {s.in?.location_name || "No location"}
                  {s.in?.within_fence === true && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
                  {s.in?.within_fence === false && <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />}
                  {s.in?.face_match === true && <ScanFace className="w-3.5 h-3.5 text-emerald-600" />}
                  {s.in?.face_match === false && <ScanFace className="w-3.5 h-3.5 text-rose-600" />}
                </span>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-muted-foreground py-6 text-center">No sessions yet today.</div>}
      </Card>
    </div>
  );
}

function SelfieDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setReady(false); setErr("");
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (!active) { stream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setReady(true);
      })
      .catch(() => setErr("Camera access denied. Please allow camera permission and try again."));
    return () => { active = false; if (streamRef.current) { streamRef.current.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; } };
  }, [open]);
  const capture = () => {
    const v = videoRef.current; if (!v) return;
    const w = 480; const h = Math.round((v.videoHeight / v.videoWidth) * w) || 360;
    const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(v, 0, 0, w, h);
    onCapture(canvas.toDataURL("image/jpeg", 0.7));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="selfie-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-4 h-4" /> Selfie verification</DialogTitle></DialogHeader>
        {err ? <div className="text-sm text-rose-600 py-4">{err}</div> : (
          <div className="space-y-3">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-black aspect-[4/3] object-cover" />
            <Button disabled={!ready} onClick={capture} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="selfie-capture"><Camera className="w-4 h-4 mr-1" /> Capture & Continue</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AttendanceToday() {
  const [data, setData] = useState(null);
  const load = () => api.get("/attendance/today-summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 15000);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const c = data.counts;
  const presentNow = c.present + c.completed;
  const rate = c.total ? Math.round((presentNow / c.total) * 100) : 0;

  const StatBox = ({ icon: Icon, label, value, tone, testid }) => (
    <div className={`p-5 rounded-xl border bg-card ${tone.border}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone.bg}`}><Icon className={`w-5 h-5 ${tone.text}`} /></div>
        <span className="text-3xl font-display font-extrabold leading-none">{value}</span>
      </div>
      <div className="text-sm font-medium text-muted-foreground">{label}</div>
    </div>
  );

  const ListCard = ({ icon: Icon, title, items, render, empty, tone }) => (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-semibold text-sm"><Icon className={`w-4 h-4 ${tone}`} /> {title}</div>
        <Badge variant="secondary" className="font-mono">{items.length}</Badge>
      </div>
      {items.length === 0 ? <div className="text-xs text-muted-foreground py-2">{empty}</div> : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((p) => <span key={p.id} className="text-xs rounded-full bg-muted px-2.5 py-1">{render(p)}</span>)}
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="att-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Workforce Snapshot</div>
          <h2 className="text-2xl font-display font-extrabold flex items-center gap-2"><UserCheck className="w-6 h-6" /> Attendance Today</h2>
          <p className="text-sm text-muted-foreground">{formatDate(data.date)}{data.is_holiday && <Badge className="ml-2 bg-rose-600">PUBLIC HOLIDAY</Badge>}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-display font-extrabold leading-none">{rate}%</div>
          <div className="text-xs text-muted-foreground">{presentNow}/{c.total} staff present</div>
        </div>
      </div>

      {data.after_start && !data.is_holiday && data.absent.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm" data-testid="absent-alert">
          <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
          <span><b className="text-rose-700">{data.absent.length} staff not clocked in</b> after work start ({data.work_start}): {data.absent.map((a) => a.name).join(", ")}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatBox icon={LogIn} label="On Duty" value={c.present} tone={{ border: "border-emerald-200", bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-600" }} testid="stat-on-duty" />
        <StatBox icon={CheckCircle2} label="Clocked Out" value={c.completed} tone={{ border: "", bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-500" }} testid="stat-clocked-out" />
        <StatBox icon={AlertTriangle} label="Late" value={c.late} tone={{ border: "border-amber-200", bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-500" }} testid="stat-late" />
        <StatBox icon={Plane} label="On Leave" value={c.on_leave} tone={{ border: "border-sky-200", bg: "bg-sky-50 dark:bg-sky-950/30", text: "text-sky-600" }} testid="stat-on-leave" />
        <StatBox icon={UserX} label="Not Clocked In" value={c.absent} tone={{ border: "border-rose-200", bg: "bg-rose-50 dark:bg-rose-950/30", text: "text-rose-500" }} testid="stat-absent" />
      </div>
      {(data.off || []).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-sm rounded-lg border bg-muted/40 px-4 py-2" data-testid="off-list">
          <Badge variant="outline" className="font-mono">REST / OFF</Badge>
          <span className="text-muted-foreground">{data.off.map((o) => o.name).join(", ")}</span>
          <span className="text-xs text-muted-foreground">(not counted absent/late today)</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        <ListCard icon={LogIn} title="On Duty Now" tone="text-emerald-600" items={data.present} empty="Nobody clocked in" render={(p) => `${p.name}${p.late ? " ⚠" : ""}`} />
        <ListCard icon={Plane} title="On Leave" tone="text-sky-600" items={data.on_leave} empty="Nobody on leave" render={(p) => `${p.name} (${p.type})`} />
        <ListCard icon={AlertTriangle} title="Late Arrivals" tone="text-amber-500" items={data.late} empty="No late arrivals" render={(p) => p.name} />
        <ListCard icon={UserX} title="Not Clocked In" tone="text-rose-500" items={data.absent} empty="Everyone accounted for" render={(p) => p.name} />
      </div>
    </div>
  );
}

function AttendanceDashboard() {
  return (
    <Tabs defaultValue="today" className="rounded-xl border bg-card p-3" data-testid="attendance-dashboard">
      <TabsList>
        <TabsTrigger value="today" data-testid="dash-tab-today">Attendance Today</TabsTrigger>
        <TabsTrigger value="calendar" data-testid="dash-tab-calendar">Leave Calendar</TabsTrigger>
      </TabsList>
      <TabsContent value="today" className="mt-3"><AttendanceToday /></TabsContent>
      <TabsContent value="calendar" className="mt-3"><LeaveCalendar /></TabsContent>
    </Tabs>
  );
}

function RecordTable({ rows }) {
  const noteOf = (r) => (r.sessions || []).flatMap((ss) => [((ss.in || {}).note || "").trim(), ((ss.out || {}).note || "").trim()]).filter(Boolean).join(" • ");
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground"><tr className="text-left">
          <th className="p-2">Date</th><th className="p-2">Staff</th><th className="p-2">First In</th><th className="p-2">Last Out</th><th className="p-2 text-right">Hours</th><th className="p-2">Location</th><th className="p-2">Note</th><th className="p-2">Flags</th>
        </tr></thead>
        <tbody data-testid="att-record-rows">
          {rows.map((r) => (
            <tr key={r.id} className="border-t hover:bg-muted/40" data-testid={`att-rec-${r.date}-${r.user_id}`}>
              <td className="p-2 whitespace-nowrap">{formatDate(r.date)}</td>
              <td className="p-2">{r.user_name}</td>
              <td className="p-2 font-mono">{t(r.first_in)}</td>
              <td className="p-2 font-mono">{t(r.last_out)}</td>
              <td className="p-2 text-right font-mono font-bold">{mins(r.work_minutes)}</td>
              <td className="p-2 text-xs">{r.sessions?.[0]?.in?.location_name || "—"}</td>
              <td className="p-2 text-xs max-w-[220px] truncate" title={noteOf(r)} data-testid={`att-note-${r.date}-${r.user_id}`}>{noteOf(r) || "—"}</td>
              <td className="p-2">{r.off_day && <Badge variant="outline" className="mr-1">OFF</Badge>}{r.open && <Badge className="bg-emerald-600 mr-1">OPEN</Badge>}{r.late && <Badge className="bg-amber-500 mr-1">LATE</Badge>}{r.fasting && <Badge className="bg-violet-600" title={r.remark || "Fasting day"} data-testid={`att-fasting-${r.date}-${r.user_id}`}><Moon className="w-3 h-3 mr-1" />FASTING</Badge>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No records.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function MyRecords() {
  const [start, setStart] = useState(ago(30));
  const [end, setEnd] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const load = () => api.get("/attendance", { params: { start, end } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [start, end]);
  useAutoRefresh(load, 20000);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="att-mine-start" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="att-mine-end" /></div>
      </div>
      <RecordTable rows={rows} />
    </div>
  );
}

function TeamRecords() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [start, setStart] = useState(ago(7));
  const [end, setEnd] = useState(todayStr());
  const [uid, setUid] = useState("all");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState([]);
  const [ltype, setLtype] = useState("Annual Leave");
  const [leaveTypes, setLeaveTypes] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent"))); }, []);
  useEffect(() => { api.get("/attendance/settings").then((r) => setLeaveTypes(r.data.leave_types || [])).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/leave/balances", { params: { type: ltype } }).then((r) => setBalances(r.data)).catch(() => {}); }, [isAdmin, ltype]);
  const load = useCallback(() => {
    const p = { start, end }; if (uid !== "all") p.user_id = uid;
    api.get("/attendance", { params: p }).then((r) => setRows(r.data));
  }, [start, end, uid]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 20000);
  const doExport = async (fmt, s, e, label) => {
    const p = { start: s, end: e, fmt }; if (uid !== "all") p.user_id = uid;
    const r = await api.get("/attendance/export", { params: p, responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `attendance_${label}.${fmt === "csv" ? "csv" : "xlsx"}`; a.click(); URL.revokeObjectURL(url);
  };
  const download = (fmt) => doExport(fmt, start, end, "custom");
  const downloadPreset = (period) => {
    const now = new Date(); const pad = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    let s = iso(now); const e = iso(now);
    if (period === "week") { const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7)); s = iso(mon); }
    else if (period === "month") { s = iso(new Date(now.getFullYear(), now.getMonth(), 1)); }
    doExport("xlsx", s, e, period);
  };
  return (
    <div className="space-y-3">
      {isAdmin && (
        <Card className="p-0 overflow-x-auto" data-testid="team-leave-balance">
          <div className="p-3 border-b font-semibold flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Team Leave Balance — {new Date().getFullYear()}</span>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={ltype} onValueChange={setLtype}><SelectTrigger className="w-44 h-8" data-testid="team-balance-type"><SelectValue /></SelectTrigger><SelectContent>{leaveTypes.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground"><tr className="text-left">
              <th className="p-2">Staff</th><th className="p-2">Type</th><th className="p-2 text-right">Quota</th><th className="p-2 text-right">Used</th><th className="p-2 text-right">Pending</th><th className="p-2 text-right">Remaining</th><th className="p-2 w-40">Usage</th>
            </tr></thead>
            <tbody data-testid="balance-rows">
              {balances.map((b) => {
                const pct = b.limited && b.quota > 0 ? Math.min(100, Math.round(((b.used + b.pending) / b.quota) * 100)) : 0;
                const low = b.limited && b.remaining <= 2;
                return (
                  <tr key={b.user_id} className={`border-t ${low ? "bg-rose-50/60 dark:bg-rose-950/20" : ""}`} data-testid={`balance-${b.user_id}`}>
                    <td className="p-2 font-medium">{b.name} <span className="text-xs text-muted-foreground">({b.role})</span></td>
                    <td className="p-2 text-xs">{b.type}</td>
                    <td className="p-2 text-right font-mono">{b.limited ? b.quota : "∞"}</td>
                    <td className="p-2 text-right font-mono">{b.used}</td>
                    <td className="p-2 text-right font-mono text-amber-700">{b.pending}</td>
                    <td className="p-2 text-right font-mono font-bold">{b.limited ? b.remaining : "∞"}{low && <Badge className="ml-2 bg-rose-600">LOW</Badge>}</td>
                    <td className="p-2"><div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${low ? "bg-rose-500" : "bg-cyan-500"}`} style={{ width: `${pct}%` }} /></div></td>
                  </tr>
                );
              })}
              {balances.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No staff.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase px-1">Quick download</span>
        <Button variant="secondary" size="sm" onClick={() => downloadPreset("today")} data-testid="att-dl-daily"><Download className="w-4 h-4 mr-1" /> Daily</Button>
        <Button variant="secondary" size="sm" onClick={() => downloadPreset("week")} data-testid="att-dl-weekly"><Download className="w-4 h-4 mr-1" /> Weekly</Button>
        <Button variant="secondary" size="sm" onClick={() => downloadPreset("month")} data-testid="att-dl-monthly"><Download className="w-4 h-4 mr-1" /> Monthly</Button>
        <span className="text-[11px] text-muted-foreground">(Excel, {uid === "all" ? "all staff" : "selected staff"})</span>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="att-team-start" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="att-team-end" /></div>
        <div><Label className="text-xs">Staff</Label>
          <Select value={uid} onValueChange={setUid}><SelectTrigger className="w-44" data-testid="att-team-staff"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All staff</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="att-export-xlsx"><Download className="w-4 h-4 mr-1" /> Excel (range)</Button>
        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-export-csv"><Download className="w-4 h-4 mr-1" /> CSV (range)</Button>
      </div>
      <RecordTable rows={rows} />
    </div>
  );
}

function LeavePanel({ manage }) {
  const { hasPerm } = useAuth();
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ type: "", start_date: todayStr(), end_date: todayStr(), reason: "" });
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [balTypes, setBalTypes] = useState([]);
  const [balSel, setBalSel] = useState("");
  const load = useCallback(() => {
    const p = {}; if (manage && filter !== "all") p.status = filter;
    api.get("/leave", { params: p }).then((r) => setRows(r.data));
  }, [manage, filter]);
  const loadBal = useCallback(() => api.get("/leave/balance-types").then((r) => setBalTypes(r.data)).catch(() => {}), []);
  useEffect(() => { api.get("/attendance/settings").then((r) => { const lt = r.data.leave_types || []; setTypes(lt); setForm((f) => ({ ...f, type: lt[0] || "" })); setBalSel((s) => s || lt[0] || ""); }); loadBal(); }, [loadBal]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leave", form); toast.success("Leave request submitted"); setForm((f) => ({ ...f, reason: "" })); load(); loadBal(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const review = async (id, status) => { await api.patch(`/leave/${id}`, { status }); toast.success(`Leave ${status}`); load(); loadBal(); };
  const del = async (id) => { if (!window.confirm("Delete this leave request?")) return; await api.delete(`/leave/${id}`); load(); loadBal(); };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="p-5 space-y-3 lg:col-span-1" data-testid="att-leave-form">
        <div className="font-semibold flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Request Leave</div>
        {balTypes.length > 0 && (() => {
          const cur = balTypes.find((b) => b.type === balSel) || balTypes[0];
          return (
            <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="leave-balance">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium">Leave balance ({cur.year})</div>
                <Select value={balSel || cur.type} onValueChange={setBalSel}><SelectTrigger className="w-36 h-7" data-testid="leave-balance-type"><SelectValue /></SelectTrigger><SelectContent>{balTypes.map((b) => <SelectItem key={b.type} value={b.type}>{b.type}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-2xl font-display font-bold" data-testid="leave-remaining">{cur.limited ? cur.remaining : "∞"}</span>
                <span className="text-xs text-muted-foreground">{cur.limited ? `of ${cur.quota} days left` : "no limit"}<br />{cur.used} used • {cur.pending} pending</span>
              </div>
            </div>
          );
        })()}
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger><SelectContent>{types.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
            <div><Label>To</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
          </div>
          <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} data-testid="leave-reason" /></div>
          <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="leave-submit">Submit Request</Button>
        </form>
      </Card>
      <Card className="p-0 lg:col-span-2 overflow-x-auto" data-testid="att-leave-list">
        {manage && (
          <div className="p-3 border-b flex items-center gap-2">
            <Label className="text-xs">Filter</Label>
            <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-36 h-8" data-testid="leave-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground"><tr className="text-left">
            {manage && <th className="p-2">Staff</th>}<th className="p-2">Type</th><th className="p-2">Dates</th><th className="p-2 text-right">Days</th><th className="p-2">Status</th><th className="p-2 text-right">Actions</th>
          </tr></thead>
          <tbody data-testid="leave-rows">
            {rows.map((l) => (
              <tr key={l.id} className="border-t" data-testid={`leave-row-${l.id}`}>
                {manage && <td className="p-2">{l.user_name}</td>}
                <td className="p-2">{l.type}</td>
                <td className="p-2 text-xs whitespace-nowrap">{formatDate(l.start_date)} → {formatDate(l.end_date)}{l.reason && <div className="text-muted-foreground">{l.reason}</div>}</td>
                <td className="p-2 text-right font-mono">{l.days}</td>
                <td className="p-2"><Badge className={LEAVE_CLS[l.status]}>{l.status.toUpperCase()}</Badge></td>
                <td className="p-2"><div className="flex justify-end gap-1">
                  {manage && l.status === "pending" && <>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-700" onClick={() => review(l.id, "approved")} data-testid={`leave-approve-${l.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-700" onClick={() => review(l.id, "rejected")} data-testid={`leave-reject-${l.id}`}><XCircle className="w-4 h-4" /></Button>
                  </>}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => del(l.id)} data-testid={`leave-del-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={manage ? 6 : 5} className="p-8 text-center text-muted-foreground">No leave requests.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
function weekBlocks(y, m) {
  return fourWeekBlocks(y, m);
}

function MyShifts() {
  const { user } = useAuth();
  const ms = user?.month_schedule || {};
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const monthData = ms[monthKey(y, m)] || {};
  const day = now.getDate();
  const dim = new Date(y, m + 1, 0).getDate();
  const bStart = day <= 7 ? 1 : day <= 14 ? 8 : day <= 21 ? 15 : 22;
  const bEnd = bStart === 22 ? dim : bStart + 6;
  const monLong = now.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const rows = [];
  for (let d = bStart; d <= bEnd; d++) rows.push(d);
  return (
    <Card className="overflow-x-auto">
      <div className="p-4 font-semibold flex items-center gap-2 border-b"><CalendarDays className="w-4 h-4" /> My Schedule — {monLong} (days {bStart}–{bEnd})</div>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground"><tr className="text-left"><th className="p-2">Date</th><th className="p-2">Status</th><th className="p-2">Clock-in Time</th></tr></thead>
        <tbody data-testid="my-shift-rows">
          {rows.map((d) => {
            const v = monthData[String(d)] || {};
            const off = !!v.off;
            const set = !!v.start;
            const wd = WD[new Date(y, m, d).getDay()];
            return (
              <tr key={d} className={`border-t ${d === day ? "bg-cyan-50/60 dark:bg-cyan-950/30" : ""}`} data-testid={`my-shift-${d}`}>
                <td className="p-2 font-mono text-xs">{wd} {d}</td>
                <td className="p-2"><Badge variant={off ? "outline" : set ? "default" : "secondary"}>{off ? "Off" : set ? "Working" : "Default"}</Badge></td>
                <td className="p-2 font-mono text-xs">{off ? "—" : set ? (v.end ? `${v.start}–${v.end}` : v.start) : "Default start"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

const ROZFA_PRESETS = [
  { label: "Rozfa 9–6", start: "09:00", end: "18:00" },
  { label: "Rozfa 10–7", start: "10:00", end: "19:00" },
  { label: "Rozfa 12–9", start: "12:00", end: "21:00" },
];

const SHIFTS = [
  { key: "9-6", label: "9–6", start: "09:00", end: "18:00" },
  { key: "10-7", label: "10–7", start: "10:00", end: "19:00" },
  { key: "12-9", label: "12–9", start: "12:00", end: "21:00" },
  { key: "10-8", label: "10–8", start: "10:00", end: "20:00" },
];
const SHIFT_BY_KEY = Object.fromEntries(SHIFTS.map((s) => [s.key, s]));
const shiftKeyOf = (ds) => (ds && SHIFTS.find((s) => s.start === ds.start && s.end === ds.end)?.key) || "none";
const uniqueRoles = (users) => Array.from(new Set(users.map((u) => u.role))).sort();

const SHIFT_COLORS = {
  "9-6": "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
  "10-7": "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  "12-9": "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
  "10-8": "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
};
const shiftKeyByStart = (start) => SHIFTS.find((s) => s.start === start)?.key || "";
const shiftKeyByLabel = (label) => { const [st, en] = String(label).split("–"); return (SHIFTS.find((s) => s.start === st && s.end === en) || SHIFTS.find((s) => s.start === st))?.key || ""; };
function fourWeekBlocks(y, m) {
  const dim = new Date(y, m + 1, 0).getDate();
  return [[1, 7], [8, 14], [15, 21], [22, dim]].map(([s, e], i) => {
    const days = []; for (let d = s; d <= e; d++) days.push(d);
    return { week: i + 1, start: s, end: e, days };
  });
}

function WeeklyTimetable({ users }) {
  const { user: me, refresh } = useAuth();
  const [uid, setUid] = useState("");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [ms, setMs] = useState({});
  const [copyTo, setCopyTo] = useState("");
  useEffect(() => { if (!uid && users[0]) setUid(users[0].id); }, [users, uid]);
  useEffect(() => { const u = users.find((x) => x.id === uid); setMs({ ...(u?.month_schedule || {}) }); }, [uid, users]);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const mk = monthKey(y, m);
  const blocks = weekBlocks(y, m);
  const monthData = ms[mk] || {};
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const monShort = cursor.toLocaleString("en-GB", { month: "short" });

  const setMonthDays = (mut) => setMs((prev) => { const cur = { ...(prev[mk] || {}) }; mut(cur); return { ...prev, [mk]: cur }; });
  const setDayVal = (day, patch) => setMonthDays((cur) => { cur[String(day)] = { ...(cur[String(day)] || {}), off: false, ...patch }; });
  const toggleOff = (day) => setMonthDays((cur) => { const ex = cur[String(day)] || {}; cur[String(day)] = ex.off ? {} : { off: true }; });
  const applyWeek = (b, start, end) => setMonthDays((cur) => { b.days.forEach((d) => { cur[String(d)] = { start, end }; }); });
  const weekOff = (b) => setMonthDays((cur) => { b.days.forEach((d) => { cur[String(d)] = { off: true }; }); });

  const save = async () => { await api.patch(`/users/${uid}`, { month_schedule: ms }); toast.success(`${monLong} timetable saved`); if (uid === me?.id) refresh(); };
  const copy = async () => {
    if (!copyTo || copyTo === uid) return toast.error("Pick a different staff to copy to");
    const target = users.find((u) => u.id === copyTo);
    const merged = { ...(target?.month_schedule || {}), [mk]: ms[mk] || {} };
    await api.patch(`/users/${copyTo}`, { month_schedule: merged });
    toast.success(`${monShort} schedule copied to ${target?.name}`);
  };

  return (
    <Card className="p-5 space-y-4" data-testid="weekly-timetable">
      <div className="font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Monthly Timetable — set each staff's clock-in time per day, week by week</div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48"><Label>Staff</Label><Select value={uid} onValueChange={setUid}><SelectTrigger data-testid="tt-staff"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="tt-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold w-40 text-center" data-testid="tt-month-label">{monLong}</span>
          <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="tt-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-1">Weeks start on the 1st. Blank days follow the default work start from Settings.</p>

      {blocks.map((b, bi) => (
        <div key={bi} className="border rounded-lg p-3 space-y-2" data-testid={`tt-week-${bi + 1}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold">Week {bi + 1} <span className="text-muted-foreground font-normal">({b.start}–{b.end} {monShort})</span></div>
            <div className="flex flex-wrap gap-1.5">
              {ROZFA_PRESETS.map((p) => <Button key={p.label} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyWeek(b, p.start, p.end)} data-testid={`tt-w${bi + 1}-preset-${p.start}`}>{p.label}</Button>)}
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => weekOff(b)} data-testid={`tt-w${bi + 1}-off`}>All Off</Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {b.days.map((d) => {
              const v = monthData[String(d)] || {};
              const off = !!v.off;
              const wd = WD[new Date(y, m, d).getDay()];
              return (
                <div key={d} className="flex items-center gap-1.5 border rounded px-2 py-1.5" data-testid={`tt-day-${d}`}>
                  <span className="w-12 text-xs font-mono text-muted-foreground shrink-0">{wd} {d}</span>
                  <Input type="time" disabled={off} value={v.start || ""} onChange={(e) => setDayVal(d, { start: e.target.value })} className="h-7 text-xs px-1" data-testid={`tt-start-${d}`} />
                  <Input type="time" disabled={off} value={v.end || ""} onChange={(e) => setDayVal(d, { end: e.target.value })} className="h-7 text-xs px-1" data-testid={`tt-end-${d}`} />
                  <Button type="button" size="sm" variant="ghost" className={`h-7 px-2 text-xs shrink-0 ${off ? "bg-slate-500 text-white hover:bg-slate-600" : "text-muted-foreground"}`} onClick={() => toggleOff(d)} data-testid={`tt-off-${d}`}>Off</Button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-end gap-3 pt-1">
        <Button onClick={save} className="bg-cyan-600 hover:bg-cyan-700" data-testid="tt-save">Save Monthly Timetable</Button>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Copy this month to</Label>
            <Select value={copyTo} onValueChange={setCopyTo}><SelectTrigger className="w-44" data-testid="tt-copy-staff"><SelectValue placeholder="Select staff" /></SelectTrigger><SelectContent>{users.filter((u) => u.id !== uid).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button type="button" variant="secondary" onClick={copy} data-testid="tt-copy-btn">Copy</Button>
        </div>
      </div>
    </Card>
  );
}

export function BestAttendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || !!user?.is_super;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const mk = `${y}-${String(m + 1).padStart(2, "0")}`;
  const load = useCallback(() => api.get("/attendance/best", { params: { month: mk } }).then((r) => setData(r.data)).catch(() => {}), [mk]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/attendance/best/winners", { params: { months: 6 } }).then((r) => setHistory(r.data)).catch(() => {}); }, []);
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const W = data?.weights || { attendance: 40, punctuality: 30, full_hours: 15, break: 10, completeness: 5 };
  const KPIS = [["attendance", "Attendance"], ["punctuality", "Punctual"], ["full_hours", "Full Hours"], ["break", "Break"], ["completeness", "Complete"]];
  const eligPct = data?.eligibility_pct ?? 50;
  const ranked = data?.ranked || [];
  const notEligible = data?.not_eligible || [];
  const now = new Date();
  const atCurrent = y > now.getFullYear() || (y === now.getFullYear() && m >= now.getMonth());
  const winners = ranked.filter((r) => r.winner);

  const printCert = (r) => {
    const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const company = esc(CONFIG.company?.name || "ROZFA GROUP SDN BHD");
    const logo = window.location.origin + "/rozfa-logo.jpg";
    const html = `<!doctype html><html><head><title>Certificate</title><meta charset="utf-8"><style>
      *{font-family:Georgia,'Times New Roman',serif;box-sizing:border-box}
      body{margin:0;padding:0}
      .cert{width:100%;min-height:96vh;margin:12px;padding:40px;border:6px double #b4891f;text-align:center;display:flex;flex-direction:column;justify-content:center;background:#fffdf6}
      .logo{height:72px;width:auto;margin:0 auto 10px;display:block}
      .co{font-size:17px;letter-spacing:2px;text-transform:uppercase;color:#1e2a66;font-weight:bold}
      .reg{font-size:12px;color:#64748b;margin-top:2px}
      .title{font-size:40px;color:#b4891f;margin:22px 0 4px;font-weight:bold}
      .sub{font-size:18px;color:#475569;margin-bottom:28px}
      .awarded{font-size:14px;color:#64748b}
      .name{font-size:46px;margin:8px 0;color:#0f172a;border-bottom:2px solid #e2c477;display:inline-block;padding:0 24px 6px}
      .month{font-size:20px;color:#334155;margin-top:18px}
      .score{font-size:16px;color:#15803d;margin-top:8px}
      .foot{margin-top:40px;font-size:12px;color:#94a3b8}
      @media print{@page{size:landscape;margin:0}}
    </style></head><body>
      <div class="cert">
        <img class="logo" src="${logo}" alt="Rozfa" />
        <div class="co">${company}</div>
        <div class="reg">202101022034 (202101022034)</div>
        <div class="title">Certificate of Excellence</div>
        <div class="sub">Best Attendance of the Month</div>
        <div class="awarded">This certificate is proudly presented to</div>
        <div class="name">${esc(r.name)}</div>
        <div class="month">for outstanding attendance in ${monLong}</div>
        <div class="score">Attendance Score: ${r.total} / 100</div>
        <div class="foot">Awarded automatically from verified clock-in records • ${new Date().toLocaleDateString("en-GB")}</div>
      </div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print");
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 600);
  };

  const monthName = (mstr) => { const [yy, mm] = mstr.split("-").map(Number); return new Date(yy, mm - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" }); };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/attendance/best/export", { params: { month: mk }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `best_attendance_${mk}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const printResults = () => {
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const company = esc(CONFIG.company?.name || "Rozfa Printing");
    const rows = ranked.map((r) => `<tr${r.winner ? ' style="background:#fef9c3;font-weight:bold"' : ""}><td>${r.rank}${r.winner ? " 🏆" : ""}</td><td style="text-align:left">${esc(r.name)}</td><td>${esc(r.role)}</td><td>${r.present_days}/${r.mandatory_days}</td><td>${r.kpis.attendance}%</td><td>${r.kpis.punctuality}%</td><td>${r.kpis.full_hours}%</td><td>${r.kpis.break}%</td><td>${r.kpis.completeness}%</td><td>${r.total}</td></tr>`).join("");
    const ne = notEligible.length ? `<h3>Not Eligible (below ${eligPct}% attendance)</h3><p>${notEligible.map((r) => `${esc(r.name)} (${r.present_days}/${r.mandatory_days})`).join(", ")}</p>` : "";
    const html = `<!doctype html><html><head><title>Best Attendance ${esc(monLong)}</title><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
      h1{margin:0;font-size:22px} .co{color:#64748b;font-size:13px;letter-spacing:1px;text-transform:uppercase}
      .sub{color:#475569;margin:2px 0 16px} table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:center} th{background:#0e7490;color:#fff}
      .w{color:#64748b;font-size:11px;margin-top:4px} @media print{@page{size:landscape;margin:12mm}}
    </style></head><body>
      <div class="co">${company}</div><h1>Best Attendance — ${esc(monLong)}</h1>
      <div class="sub">Weights: Attendance ${W.attendance}% • Punctuality ${W.punctuality}% • Full Hours ${W.full_hours}% • Break ${W.break}% • Completeness ${W.completeness}% • Eligibility ≥ ${eligPct}%</div>
      <table><thead><tr><th>Rank</th><th>Staff</th><th>Role</th><th>Present</th><th>Att.</th><th>Punct.</th><th>Full Hrs</th><th>Break</th><th>Compl.</th><th>Total</th></tr></thead><tbody>${rows || '<tr><td colspan="10">No eligible staff</td></tr>'}</tbody></table>
      ${ne}
      <p class="w">Generated ${new Date().toLocaleString("en-GB")} • Ranked from verified clock-in records.</p>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print");
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-4" data-testid="best-attendance">
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Best Attendance of the Month</div>
          <p className="text-xs text-muted-foreground max-w-xl">Ranked purely from clock-in records. Approved leave, MC and official duty never count against staff. Weights: Attendance {W.attendance}%, Punctuality {W.punctuality}%, Full Hours {W.full_hours}%, Break {W.break}%, Completeness {W.completeness}%. Eligible at ≥ {eligPct}% attendance.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && <Badge className={data.future ? "bg-sky-600" : data.is_current ? "bg-emerald-600" : "bg-slate-500"}>{data.future ? "Upcoming" : data.is_current ? "Live preview" : "Locked"}</Badge>}
          {!data?.future && (ranked.length > 0 || notEligible.length > 0) && (<>
            {isAdmin && <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="best-export-xlsx"><Download className="w-4 h-4 mr-1" /> Excel</Button>}
            <Button size="sm" variant="outline" onClick={printResults} data-testid="best-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          </>)}
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="best-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold w-36 text-center" data-testid="best-month">{monLong}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={atCurrent} onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="best-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </Card>

      {winners.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-950/40 dark:to-amber-900/20 border-amber-300 flex items-center gap-3" data-testid="best-announce">
          <Trophy className="w-8 h-8 text-amber-500 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold">{winners.map((w) => w.name).join(" & ")}</span> {winners.length > 1 ? "share" : "is"} the Best Attendance for <span className="font-semibold">{monLong}</span> with a score of <span className="font-semibold">{winners[0].total}/100</span>! 🎉
          </div>
        </Card>
      )}

      {ranked.length === 0 && <Card className="p-10 text-center text-muted-foreground" data-testid="best-empty">{data?.future ? "This month hasn't started yet." : "No eligible staff ranked for this month."}</Card>}

      <div className="grid gap-3">
        {ranked.map((r) => (
          <Card key={r.user_id} className={`p-4 ${r.winner ? "border-2 border-amber-400 bg-amber-50/50 dark:bg-amber-950/20" : ""}`} data-testid={`best-row-${r.user_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${r.winner ? "bg-amber-400 text-slate-900" : "bg-muted"}`}>{r.winner ? <Trophy className="w-5 h-5" /> : r.rank}</div>
                <div>
                  <div className="font-semibold flex items-center gap-2">{r.name} {r.winner && <Badge className="bg-amber-500" data-testid={`best-winner-${r.user_id}`}>Winner</Badge>}</div>
                  <div className="text-[11px] text-muted-foreground font-mono uppercase">{r.role} • present {r.present_days}/{r.mandatory_days} days</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {r.winner && <Button size="sm" variant="outline" className="border-amber-400 text-amber-700" onClick={() => printCert(r)} data-testid={`best-cert-${r.user_id}`}><Printer className="w-4 h-4 mr-1" /> Certificate</Button>}
                <div className="text-right"><div className="text-2xl font-bold" data-testid={`best-total-${r.user_id}`}>{r.total}</div><div className="text-[10px] text-muted-foreground">/ 100</div></div>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 mt-3">
              {KPIS.map(([k, label]) => (
                <div key={k} className="text-center rounded-md bg-muted/50 py-1.5">
                  <div className="text-sm font-semibold">{r.kpis[k]}%</div>
                  <div className="text-[10px] text-muted-foreground">{label}</div>
                  <div className="text-[9px] text-muted-foreground/70">weight {W[k]}%</div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {notEligible.length > 0 && (
        <Card className="p-4" data-testid="best-not-eligible">
          <div className="text-sm font-semibold mb-2 text-muted-foreground">Not eligible this month (below {eligPct}% attendance / mid-month joiners)</div>
          <div className="flex flex-wrap gap-2">
            {notEligible.map((r) => <Badge key={r.user_id} variant="outline">{r.name} — {r.present_days}/{r.mandatory_days}</Badge>)}
          </div>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="p-4" data-testid="best-history">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Past Winners</div>
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.month} className="flex items-center justify-between py-1.5 text-sm" data-testid={`best-history-${h.month}`}>
                <span className="text-muted-foreground w-24">{monthName(h.month)}{h.is_current && <span className="text-[10px] text-emerald-600"> (live)</span>}</span>
                <span className="flex-1 font-medium">{h.winners.length ? h.winners.join(" & ") : <span className="text-muted-foreground font-normal">—</span>}</span>
                {h.total != null && <span className="text-xs text-muted-foreground">{h.total}/100</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


function RosterHolidays() {
  const [users, setUsers] = useState([]);
  const loadUsers = useCallback(() => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent"))), []);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  return (
    <Tabs defaultValue="quick" className="space-y-3">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="quick" data-testid="tt-sub-quick">Quick Assign</TabsTrigger>
        <TabsTrigger value="rotation" data-testid="tt-sub-rotation">Auto-Rotation</TabsTrigger>
        <TabsTrigger value="fixed" data-testid="tt-sub-fixed">Fixed Shifts</TabsTrigger>
        <TabsTrigger value="fasting" data-testid="tt-sub-fasting">Fasting Days</TabsTrigger>
        <TabsTrigger value="editor" data-testid="tt-sub-editor">Day Editor</TabsTrigger>
      </TabsList>
      <TabsContent value="quick"><QuickAssign users={users} onApplied={loadUsers} /></TabsContent>
      <TabsContent value="rotation"><RotationGroups users={users} onApplied={loadUsers} /></TabsContent>
      <TabsContent value="fixed"><FixedShifts users={users} onChanged={loadUsers} /></TabsContent>
      <TabsContent value="fasting"><FastingDays users={users} /></TabsContent>
      <TabsContent value="editor"><WeeklyTimetable users={users} /></TabsContent>
    </Tabs>
  );
}

const ShiftChip = ({ k }) => {
  const s = SHIFT_BY_KEY[k];
  if (!s) return <span className="text-muted-foreground">—</span>;
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-mono ${SHIFT_COLORS[k]}`}>{s.label}</span>;
};

function QuickAssign({ users, onApplied }) {
  const [shift, setShift] = useState("9-6");
  const [role, setRole] = useState("all");
  const [sel, setSel] = useState(() => new Set());
  const [period, setPeriod] = useState("week");
  const [weekSel, setWeekSel] = useState(() => new Set());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const roles = uniqueRoles(users);
  const filtered = users.filter((u) => role === "all" || u.role === role);
  const allSel = filtered.length > 0 && filtered.every((u) => sel.has(u.id));
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((p) => { const n = new Set(p); if (allSel) filtered.forEach((u) => n.delete(u.id)); else filtered.forEach((u) => n.add(u.id)); return n; });
  const y = cursor.getFullYear(), m = cursor.getMonth(), mk = monthKey(y, m);
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const monShort = cursor.toLocaleString("en-GB", { month: "short" });
  const wb = fourWeekBlocks(y, m);
  const toggleWeek = (w) => setWeekSel((p) => { const n = new Set(p); n.has(w) ? n.delete(w) : n.add(w); return n; });
  const range = () => {
    if (period === "month") { const dim = new Date(y, m + 1, 0).getDate(); return [`${mk}-01`, `${mk}-${String(dim).padStart(2, "0")}`]; }
    if (period === "custom") return [from, to];
    const blk = blockOf(new Date()); return [blk.days[0].ds, blk.days[blk.days.length - 1].ds];
  };
  const [rs, re] = range();
  const apply = async () => {
    if (sel.size === 0) return toast.error("Select at least one staff");
    const payload = { user_ids: [...sel], shift, sunday_rest: true };
    let desc = "";
    if (period === "weeks") {
      if (weekSel.size === 0) return toast.error("Tick at least one week");
      const dates = [];
      wb.forEach((b) => { if (weekSel.has(b.week)) b.days.forEach((d) => dates.push(`${mk}-${String(d).padStart(2, "0")}`)); });
      payload.dates = dates;
      desc = `week ${[...weekSel].sort((a, b) => a - b).join(", ")} of ${monLong}`;
    } else {
      payload.start_date = rs; payload.end_date = re; desc = `${rs} → ${re}`;
    }
    setBusy(true);
    try {
      const r = await api.post("/timetable/bulk-assign", payload);
      toast.success(`Applied ${shift === "off" ? "Off" : SHIFT_BY_KEY[shift].label} to ${r.data.updated} staff (${desc})`);
      setSel(new Set()); setWeekSel(new Set());
      onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to apply"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="quick-assign">
      <div className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Quick Assign — set one shift for many staff at once</div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">Shift</Label>
          <Select value={shift} onValueChange={setShift}>
            <SelectTrigger data-testid="qa-shift" className={shift !== "off" ? SHIFT_COLORS[shift] : ""}><SelectValue /></SelectTrigger>
            <SelectContent>
              {SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} ({s.start}–{s.end})</SelectItem>)}
              <SelectItem value="off">Off (rest day)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="qa-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
              <SelectItem value="weeks">By week</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Filter by role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="qa-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((rl) => <SelectItem key={rl} value={rl}>{rl}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {(period === "month" || period === "weeks") && (
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="qa-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold w-40 text-center" data-testid="qa-month-label">{monLong}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="qa-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
      {period === "weeks" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="qa-weeks">
          {wb.map((b) => (
            <label key={b.week} className="flex items-center gap-2 border rounded px-3 py-2 text-sm cursor-pointer hover:bg-muted/50" data-testid={`qa-week-${b.week}`}>
              <Checkbox checked={weekSel.has(b.week)} onCheckedChange={() => toggleWeek(b.week)} />
              <span>Week {b.week}</span>
              <span className="ml-auto text-[11px] text-muted-foreground font-mono">{b.start}–{b.end} {monShort}</span>
            </label>
          ))}
        </div>
      )}
      {period === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="qa-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="qa-to" /></div>
        </div>
      )}
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="qa-select-all" /> Select all ({filtered.length})
          </label>
          <span className="text-xs text-muted-foreground" data-testid="qa-sel-count">{sel.size} selected</span>
        </div>
        <div className="p-2 max-h-72 overflow-y-auto space-y-2" data-testid="qa-staff-list">
          {roles.filter((rl) => role === "all" || rl === role).map((rl) => {
            const group = filtered.filter((u) => u.role === rl);
            if (group.length === 0) return null;
            const groupAllSel = group.every((u) => sel.has(u.id));
            const toggleGroup = () => setSel((p) => { const n = new Set(p); if (groupAllSel) group.forEach((u) => n.delete(u.id)); else group.forEach((u) => n.add(u.id)); return n; });
            return (
              <div key={rl} data-testid={`qa-role-group-${rl}`}>
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground px-1 py-1 cursor-pointer">
                  <Checkbox checked={groupAllSel} onCheckedChange={toggleGroup} data-testid={`qa-role-select-all-${rl}`} />
                  {rl} ({group.length})
                </label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
                  {group.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 border rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50" data-testid={`qa-staff-${u.id}`}>
                      <Checkbox checked={sel.has(u.id)} onCheckedChange={() => toggle(u.id)} />
                      <span className="truncate">{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">No staff for this role.</div>}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={apply} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="qa-apply">Apply to {sel.size} staff</Button>
        <span className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">Will set {shift === "off" ? <b>Off</b> : <ShiftChip k={shift} />} for <b>{period === "weeks" ? (weekSel.size ? `week ${[...weekSel].sort((a, b) => a - b).join(", ")} of ${monShort}` : "— pick week(s)") : `${rs} → ${re}`}</b>. Sundays are kept as rest.</span>
      </div>
    </Card>
  );
}

function fastDatesLabel(dates) {
  if (!dates || dates.length === 0) return "—";
  const s = [...dates].sort();
  if (s.length === 1) return s[0];
  return `${s[0]} → ${s[s.length - 1]} (${s.length} days)`;
}

function FastingStatusBadge({ status }) {
  const map = { pending: "bg-amber-500", approved: "bg-emerald-600", rejected: "bg-rose-600", cancelled: "bg-slate-400" };
  return <Badge className={map[status] || "bg-slate-400"}>{status}</Badge>;
}

function MyFasting() {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/attendance/fasting/requests").then((r) => setRows(r.data || [])); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!from || !to) return toast.error("Pick a date or date range");
    setBusy(true);
    try {
      await api.post("/attendance/fasting/requests", { start_date: from, end_date: to });
      toast.success("Fasting request submitted — waiting for admin approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/attendance/fasting/requests/${id}`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="my-fasting">
      <div className="font-semibold flex items-center gap-2"><Moon className="w-4 h-4" /> Request fasting days</div>
      <p className="text-xs text-muted-foreground -mt-2">Submit the day(s) you will be fasting. Once an admin approves, you may skip your break and clock out early on those days without penalty.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="myfast-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="myfast-to" /></div>
        <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="myfast-submit"><Plus className="w-4 h-4 mr-1" /> Submit request</Button>
      </div>
      <div className="border rounded-lg">
        <div className="px-3 py-2 border-b bg-muted/40 text-xs font-semibold uppercase text-muted-foreground">My requests</div>
        <div className="divide-y" data-testid="myfast-list">
          {rows.length === 0 && <div className="p-4 text-sm text-muted-foreground">No requests yet.</div>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`myfast-row-${r.id}`}>
              <span className="font-mono text-xs flex-1">{fastDatesLabel(r.dates)}</span>
              <FastingStatusBadge status={r.status} />
              {r.status === "rejected" && r.note && <span className="text-xs text-rose-600 italic max-w-[12rem] truncate" title={r.note}>“{r.note}”</span>}
              {r.status === "pending" && <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700" onClick={() => cancel(r.id)} data-testid={`myfast-cancel-${r.id}`}>Cancel</Button>}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function FastingRequestsAdmin({ onDecided }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => { api.get("/attendance/fasting/requests", { params: { status: "pending" } }).then((r) => setRows(r.data || [])); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, action) => {
    let note = "";
    if (action === "reject") { note = window.prompt("Reason for rejecting (optional):") || ""; }
    setBusy(id);
    try {
      await api.post(`/attendance/fasting/requests/${id}/decide`, { action, note });
      toast.success(action === "approve" ? "Approved — fasting days activated" : "Request rejected");
      load(); onDecided && onDecided();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  if (rows.length === 0) return null;
  return (
    <div className="border rounded-lg border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2" data-testid="fasting-pending">
      <div className="text-xs font-semibold uppercase text-amber-700 dark:text-amber-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Pending staff requests ({rows.length})</div>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 text-sm bg-background rounded px-3 py-2 border" data-testid={`fasting-req-${r.id}`}>
          <span className="font-medium w-40 truncate">{r.user_name}</span>
          <span className="text-[11px] font-mono uppercase text-muted-foreground">{r.role}</span>
          <span className="font-mono text-xs flex-1">{fastDatesLabel(r.dates)}</span>
          <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={busy === r.id} onClick={() => decide(r.id, "approve")} data-testid={`fasting-approve-${r.id}`}>Approve</Button>
          <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700" disabled={busy === r.id} onClick={() => decide(r.id, "reject")} data-testid={`fasting-reject-${r.id}`}>Reject</Button>
        </div>
      ))}
    </div>
  );
}

function FastingDays({ users }) {
  const [role, setRole] = useState("all");
  const [sel, setSel] = useState(() => new Set());
  const [period, setPeriod] = useState("custom");
  const [weekSel, setWeekSel] = useState(() => new Set());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const [marks, setMarks] = useState([]);
  const roles = uniqueRoles(users);
  const filtered = users.filter((u) => role === "all" || u.role === role);
  const allSel = filtered.length > 0 && filtered.every((u) => sel.has(u.id));
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((p) => { const n = new Set(p); if (allSel) filtered.forEach((u) => n.delete(u.id)); else filtered.forEach((u) => n.add(u.id)); return n; });
  const y = cursor.getFullYear(), m = cursor.getMonth(), mk = monthKey(y, m);
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const monShort = cursor.toLocaleString("en-GB", { month: "short" });
  const wb = fourWeekBlocks(y, m);
  const toggleWeek = (w) => setWeekSel((p) => { const n = new Set(p); n.has(w) ? n.delete(w) : n.add(w); return n; });
  const nameById = Object.fromEntries(users.map((u) => [u.id, u.name]));
  const loadMarks = useCallback(() => {
    const dim = new Date(y, m + 1, 0).getDate();
    api.get("/attendance/fasting", { params: { start: `${mk}-01`, end: `${mk}-${String(dim).padStart(2, "0")}` } }).then((r) => setMarks(r.data || []));
  }, [mk, y, m]);
  useEffect(() => { loadMarks(); }, [loadMarks]);
  const range = () => {
    if (period === "month") { const dim = new Date(y, m + 1, 0).getDate(); return [`${mk}-01`, `${mk}-${String(dim).padStart(2, "0")}`]; }
    return [from, to];
  };
  const [rs, re] = range();
  const submit = async (mark) => {
    if (sel.size === 0) return toast.error("Select at least one staff");
    const payload = { user_ids: [...sel], mark };
    let desc = "";
    if (period === "weeks") {
      if (weekSel.size === 0) return toast.error("Tick at least one week");
      const dates = [];
      wb.forEach((b) => { if (weekSel.has(b.week)) b.days.forEach((d) => dates.push(`${mk}-${String(d).padStart(2, "0")}`)); });
      payload.dates = dates;
      desc = `week ${[...weekSel].sort((a, b) => a - b).join(", ")} of ${monLong}`;
    } else {
      payload.start_date = rs; payload.end_date = re; desc = `${rs} → ${re}`;
    }
    setBusy(true);
    try {
      const r = await api.post("/attendance/fasting", payload);
      toast.success(`${mark ? "Marked" : "Unmarked"} fasting for ${r.data.updated} staff-days (${desc})`);
      setSel(new Set()); setWeekSel(new Set());
      loadMarks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const removeMark = async (uid, date) => {
    try { await api.post("/attendance/fasting", { user_ids: [uid], dates: [date], mark: false }); loadMarks(); }
    catch { toast.error("Failed to remove"); }
  };
  const marksByUser = marks.reduce((acc, mm) => { (acc[mm.user_id] = acc[mm.user_id] || []).push(mm.date); return acc; }, {});
  return (
    <Card className="p-5 space-y-4" data-testid="fasting-days">
      <div className="font-semibold flex items-center gap-2"><Moon className="w-4 h-4" /> Fasting Days — mark staff who are fasting so they may leave early without penalty</div>
      <p className="text-xs text-muted-foreground -mt-2">Only staff marked here are allowed to skip break and clock out early on the chosen dates. Default allowance is set in Settings.</p>
      <FastingRequestsAdmin onDecided={loadMarks} />
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="fast-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Date range</SelectItem>
              <SelectItem value="month">Whole month</SelectItem>
              <SelectItem value="weeks">By week</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Filter by role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="fast-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((rl) => <SelectItem key={rl} value={rl}>{rl}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {(period === "month" || period === "weeks") && (
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="fast-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold w-40 text-center" data-testid="fast-month-label">{monLong}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="fast-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
      {period === "weeks" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="fast-weeks">
          {wb.map((b) => (
            <label key={b.week} className="flex items-center gap-2 border rounded px-3 py-2 text-sm cursor-pointer hover:bg-muted/50" data-testid={`fast-week-${b.week}`}>
              <Checkbox checked={weekSel.has(b.week)} onCheckedChange={() => toggleWeek(b.week)} />
              <span>Week {b.week}</span>
              <span className="ml-auto text-[11px] text-muted-foreground font-mono">{b.start}–{b.end} {monShort}</span>
            </label>
          ))}
        </div>
      )}
      {period === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="fast-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="fast-to" /></div>
        </div>
      )}
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="fast-select-all" /> Select all ({filtered.length})
          </label>
          <span className="text-xs text-muted-foreground" data-testid="fast-sel-count">{sel.size} selected</span>
        </div>
        <div className="p-2 max-h-72 overflow-y-auto space-y-2" data-testid="fast-staff-list">
          {roles.filter((rl) => role === "all" || rl === role).map((rl) => {
            const group = filtered.filter((u) => u.role === rl);
            if (group.length === 0) return null;
            const groupAllSel = group.every((u) => sel.has(u.id));
            const toggleGroup = () => setSel((p) => { const n = new Set(p); if (groupAllSel) group.forEach((u) => n.delete(u.id)); else group.forEach((u) => n.add(u.id)); return n; });
            return (
              <div key={rl} data-testid={`fast-role-group-${rl}`}>
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground px-1 py-1 cursor-pointer">
                  <Checkbox checked={groupAllSel} onCheckedChange={toggleGroup} data-testid={`fast-role-select-all-${rl}`} />
                  {rl} ({group.length})
                </label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
                  {group.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 border rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50" data-testid={`fast-staff-${u.id}`}>
                      <Checkbox checked={sel.has(u.id)} onCheckedChange={() => toggle(u.id)} />
                      <span className="truncate">{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">No staff for this role.</div>}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={() => submit(true)} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="fast-mark"><Moon className="w-4 h-4 mr-1" /> Mark Fasting ({sel.size})</Button>
        <Button onClick={() => submit(false)} disabled={busy} variant="outline" className="border-rose-300 text-rose-700" data-testid="fast-unmark">Unmark</Button>
        <span className="text-xs text-muted-foreground">Applies to <b>{period === "weeks" ? (weekSel.size ? `week ${[...weekSel].sort((a, b) => a - b).join(", ")} of ${monShort}` : "— pick week(s)") : `${rs} → ${re}`}</b></span>
      </div>
      <div className="border rounded-lg p-3 space-y-2" data-testid="fast-current">
        <div className="text-xs font-semibold text-muted-foreground uppercase">Fasting marks in {monLong}</div>
        {Object.keys(marksByUser).length === 0 && <div className="text-xs text-muted-foreground">No fasting marks this month.</div>}
        {Object.entries(marksByUser).map(([uid, dates]) => (
          <div key={uid} className="flex flex-wrap items-center gap-1.5 text-sm" data-testid={`fast-mark-row-${uid}`}>
            <span className="font-medium w-40 truncate">{nameById[uid] || uid}</span>
            <div className="flex flex-wrap gap-1">
              {dates.sort().map((d) => (
                <span key={d} className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300 pl-2 pr-1 py-0.5 text-[11px] font-mono" data-testid={`fast-chip-${uid}-${d}`}>
                  {d.slice(8)}/{d.slice(5, 7)}
                  <button type="button" onClick={() => removeMark(uid, d)} className="rounded-full p-0.5 hover:bg-rose-200 text-rose-600" data-testid={`fast-chip-del-${uid}-${d}`}><XCircle className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}


function FixedShifts({ users, onChanged }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [busy, setBusy] = useState(false);
  const mk = monthKey(cursor.getFullYear(), cursor.getMonth());
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const setDefault = async (u, key) => {
    try { await api.post(`/timetable/fixed/set`, { user_id: u.id, shift: key }); toast.success(`${u.name}: default shift ${key === "none" ? "cleared" : SHIFT_BY_KEY[key].label}`); onChanged?.(); }
    catch { toast.error("Failed to update"); }
  };
  const applyMonth = async () => {
    setBusy(true);
    try { const r = await api.post("/timetable/fixed/apply", { month: mk }); toast.success(`Filled ${monLong} for ${r.data.updated} fixed-shift staff`); onChanged?.(); }
    catch { toast.error("Failed to fill month"); } finally { setBusy(false); }
  };
  const fixedCount = users.filter((u) => u.default_shift?.start).length;
  return (
    <Card className="p-5 space-y-4" data-testid="fixed-shifts">
      <div className="font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4" /> Fixed Shifts — staff who keep the same shift every week</div>
      <p className="text-xs text-muted-foreground -mt-2">Give a staff a default shift and they are excluded from rotation. Use "Fill month" to auto-apply it across a month (Sundays kept as rest).</p>
      <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="fixed-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-3 py-2" data-testid={`fixed-row-${u.id}`}>
            <div className="min-w-0"><div className="text-sm font-medium truncate">{u.name}</div><div className="text-[10px] text-muted-foreground font-mono uppercase">{u.role}</div></div>
            <Select value={shiftKeyOf(u.default_shift)} onValueChange={(v) => setDefault(u, v)}>
              <SelectTrigger className="ml-auto w-44 h-8" data-testid={`fixed-select-${u.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No fixed shift</SelectItem>
                {SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} ({s.start}–{s.end})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        {users.length === 0 && <div className="p-4 text-sm text-muted-foreground">No staff.</div>}
      </div>
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="fixed-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm font-semibold w-40 text-center" data-testid="fixed-month-label">{monLong}</span>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="fixed-next-month"><ChevronRight className="w-4 h-4" /></Button>
        <Button onClick={applyMonth} disabled={busy || fixedCount === 0} className="bg-cyan-600 hover:bg-cyan-700" data-testid="fixed-apply">Fill {monLong} ({fixedCount} staff)</Button>
      </div>
    </Card>
  );
}

function RotationGroups({ users, onApplied }) {
  const [groups, setGroups] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [preview, setPreview] = useState(null);
  const [auto, setAuto] = useState([]);
  const [grid, setGrid] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => api.get("/timetable/rotation").then((r) => { setGroups(r.data.groups || []); setDirty(false); }), []);
  useEffect(() => { load(); }, [load]);
  const mk = monthKey(cursor.getFullYear(), cursor.getMonth());
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const roles = uniqueRoles(users);
  const rotatable = users.filter((u) => !u.default_shift?.start);
  const mut = (i, patch) => { setGroups((g) => g.map((x, idx) => (idx === i ? { ...x, ...patch } : x))); setDirty(true); };
  const addGroup = () => { setGroups((g) => [...g, { id: null, name: "New group", member_ids: [], shifts: ["9-6", "12-9"], cadence: "weekly" }]); setDirty(true); };
  const removeGroup = (i) => { setGroups((g) => g.filter((_, idx) => idx !== i)); setDirty(true); };
  const toggleShift = (i, k) => { const g = groups[i]; mut(i, { shifts: g.shifts.includes(k) ? g.shifts.filter((x) => x !== k) : [...g.shifts, k] }); };
  const toggleMember = (i, id) => { const g = groups[i]; mut(i, { member_ids: g.member_ids.includes(id) ? g.member_ids.filter((x) => x !== id) : [...g.member_ids, id] }); };
  const addRole = (i, role) => { const ids = rotatable.filter((u) => u.role === role).map((u) => u.id); const g = groups[i]; mut(i, { member_ids: Array.from(new Set([...g.member_ids, ...ids])) }); };
  const save = async () => { try { const r = await api.put("/timetable/rotation", { groups }); setGroups(r.data.groups); setDirty(false); toast.success("Rotation groups saved"); } catch { toast.error("Failed to save"); } };
  const generate = async (g) => {
    if (!g.id || dirty) return toast.error("Save groups first");
    if (g.cadence === "3day") {
      setBusy(true);
      try { const r = await api.post("/timetable/rotation/apply-auto", { group_id: g.id, month: mk }); toast.success(`3-day rotation applied to ${r.data.updated} staff for ${monLong}`); onApplied?.(); }
      catch (e) { toast.error(e?.response?.data?.detail || "Cannot apply"); } finally { setBusy(false); }
      return;
    }
    try {
      const r = await api.post("/timetable/rotation/generate", { group_id: g.id, month: mk });
      const rows = r.data.rows.map((row) => ({ user_id: row.user_id, name: row.name, weeks: row.weeks.map((w) => ({ week: w.week, shift: w.shift })) }));
      setAuto(rows.map((row) => ({ ...row, weeks: row.weeks.map((w) => ({ ...w })) })));
      setGrid(rows.map((row) => ({ ...row, weeks: row.weeks.map((w) => ({ ...w })) })));
      setPreview({ group: r.data.group, month: r.data.month, cadence: r.data.cadence, group_id: g.id });
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot generate"); }
  };
  const setCell = (uid, week, shift) => setGrid((gr) => gr.map((r) => (r.user_id === uid ? { ...r, weeks: r.weeks.map((w) => (w.week === week ? { ...w, shift } : w)) } : r)));
  const resetAuto = () => setGrid(auto.map((r) => ({ ...r, weeks: r.weeks.map((w) => ({ ...w })) })));
  const applyPreview = async () => {
    setBusy(true);
    try {
      const assignments = grid.map((r) => ({ user_id: r.user_id, weeks: r.weeks.map((w) => ({ week: w.week, shift: w.shift })) }));
      const r = await api.post("/timetable/rotation/apply", { month: preview.month, assignments });
      toast.success(`Rotation saved for ${r.data.updated} staff`); setPreview(null); onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot apply"); } finally { setBusy(false); }
  };
  const [py, pm] = (preview?.month || "0-0").split("-").map(Number);
  const wcols = preview ? fourWeekBlocks(py, pm - 1) : [];
  const pMonShort = preview ? new Date(py, pm - 1, 1).toLocaleString("en-GB", { month: "short" }) : "";
  return (
    <Card className="p-5 space-y-4" data-testid="rotation-groups">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold flex items-center gap-2"><CalendarRange className="w-4 h-4" /> Auto-Rotation — spread a team across shifts and swap each cycle</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={addGroup} data-testid="rot-add-group"><Plus className="w-4 h-4 mr-1" /> Group</Button>
          <Button size="sm" onClick={save} disabled={!dirty} className="bg-cyan-600 hover:bg-cyan-700" data-testid="rot-save">Save groups</Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Generate for</span>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="rot-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm font-semibold w-40 text-center" data-testid="rot-month-label">{monLong}</span>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="rot-next-month"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      {groups.length === 0 && <div className="text-sm text-muted-foreground border rounded-lg p-6 text-center">No rotation groups yet. Add one to get started.</div>}
      {groups.map((g, i) => (
        <div key={g.id || i} className="border rounded-lg p-4 space-y-3" data-testid={`rot-group-${i}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Input value={g.name} onChange={(e) => mut(i, { name: e.target.value })} className="h-8 w-48 font-semibold" data-testid={`rot-name-${i}`} />
            <Select value={g.cadence} onValueChange={(v) => mut(i, { cadence: v })}>
              <SelectTrigger className="h-8 w-32" data-testid={`rot-cadence-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="weekly">Weekly swap</SelectItem><SelectItem value="monthly">Monthly swap</SelectItem><SelectItem value="3day">Every 3 days (Mon–Wed / Thu–Sat)</SelectItem></SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              {SHIFTS.map((s) => (
                <Button key={s.key} type="button" size="sm" variant={g.shifts.includes(s.key) ? "default" : "outline"} className={`h-8 text-xs ${g.shifts.includes(s.key) ? "bg-cyan-600 hover:bg-cyan-700" : ""}`} onClick={() => toggleShift(i, s.key)} data-testid={`rot-shift-${i}-${s.key}`}>{s.label}</Button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1">
              <Button type="button" size="sm" variant="outline" onClick={() => generate(g)} data-testid={`rot-generate-${i}`}>Generate</Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => removeGroup(i)} data-testid={`rot-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground">Members ({g.member_ids.length}):</span>
            <Select value="" onValueChange={(v) => addRole(i, v)}>
              <SelectTrigger className="h-7 w-40 text-xs" data-testid={`rot-addrole-${i}`}><SelectValue placeholder="+ Add all of role" /></SelectTrigger>
              <SelectContent>{roles.map((rl) => <SelectItem key={rl} value={rl}>{rl}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
            {rotatable.map((u) => (
              <label key={u.id} className="flex items-center gap-2 border rounded px-2 py-1 text-sm cursor-pointer hover:bg-muted/50" data-testid={`rot-member-${i}-${u.id}`}>
                <Checkbox checked={g.member_ids.includes(u.id)} onCheckedChange={() => toggleMember(i, u.id)} />
                <span className="truncate">{u.name}</span>
                <span className="ml-auto text-[10px] text-muted-foreground font-mono uppercase shrink-0">{u.role}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Fixed-shift staff are excluded from rotation and don't appear here.</p>
          {g.cadence === "3day" && <p className="text-[11px] text-emerald-700 dark:text-emerald-400">Every 3 days: Mon–Wed on the first chosen shift, Thu–Sat on the next (members spread for coverage). "Generate" applies it directly — Sundays stay off.</p>}
        </div>
      ))}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="rot-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.group} — {preview?.month} ({preview?.cadence})</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Each cell is a dropdown — change any week's shift (or set Off) before saving. Sundays stay as rest.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase font-mono text-muted-foreground border-b"><th className="p-2 text-left">Staff</th>{wcols.map((b) => <th key={b.week} className="p-2 text-center">Week {b.week}<div className="font-sans normal-case text-[10px] text-muted-foreground">{b.start}–{b.end} {pMonShort}</div></th>)}</tr></thead>
              <tbody data-testid="rot-preview-rows">
                {grid.map((r) => (
                  <tr key={r.user_id} className="border-b">
                    <td className="p-2 font-medium whitespace-nowrap">{r.name}</td>
                    {r.weeks.map((w) => (
                      <td key={w.week} className="p-2 text-center" data-testid={`rot-cell-${r.user_id}-${w.week}`}>
                        <Select value={w.shift} onValueChange={(v) => setCell(r.user_id, w.week, v)}>
                          <SelectTrigger className={`h-8 w-24 mx-auto text-xs ${w.shift === "off" ? "text-muted-foreground" : SHIFT_COLORS[w.shift] || ""}`} data-testid={`rot-cell-trigger-${r.user_id}-${w.week}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                            <SelectItem value="off">Off</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center gap-2 pt-2">
            <Button variant="ghost" onClick={resetAuto} data-testid="rot-preview-reset">Reset to auto</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="rot-preview-cancel">Cancel</Button>
              <Button onClick={applyPreview} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="rot-preview-apply">Save to timetable</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function blockOf(anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  const day = anchor.getDate();
  const dim = new Date(y, m + 1, 0).getDate();
  const bStart = day <= 7 ? 1 : day <= 14 ? 8 : day <= 21 ? 15 : 22;
  const bEnd = bStart === 22 ? dim : bStart + 6;
  const days = [];
  for (let d = bStart; d <= bEnd; d++) { const dt = new Date(y, m, d); days.push({ d, date: dt, ds: fmtDate(dt), wd: WD[dt.getDay()] }); }
  return { y, m, bStart, bEnd, dim, days };
}

function resolveCellMonth(ms, dateObj, defaultStart) {
  const dd = ((ms || {})[monthKey(dateObj.getFullYear(), dateObj.getMonth())] || {})[String(dateObj.getDate())];
  if (dd) {
    if (dd.off) return { kind: "off" };
    if (dd.start) return { kind: "work", label: dd.end ? `${dd.start}–${dd.end}` : dd.start };
  }
  return { kind: "default", label: defaultStart };
}

function TeamWeek() {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [fasting, setFasting] = useState([]);
  const [anchor, setAnchor] = useState(() => new Date());
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent")));
    api.get("/attendance/settings").then((r) => setSettings(r.data));
    api.get("/leave", { params: { status: "approved" } }).then((r) => setLeaves(r.data)).catch(() => {});
  }, []);
  const blk = blockOf(anchor);
  const days = blk.days;
  useEffect(() => {
    if (days.length === 0) return;
    api.get("/attendance/fasting", { params: { start: days[0].ds, end: days[days.length - 1].ds } })
      .then((r) => setFasting(r.data || [])).catch(() => {});
  }, [days[0]?.ds, days[days.length - 1]?.ds]);
  const fastSet = new Set(fasting.map((f) => `${f.user_id}|${f.date}`));
  const isFast = (uid, ds) => fastSet.has(`${uid}|${ds}`);
  const defaultStart = settings.work_start || "09:00";
  const onLeave = (uid, ds) => leaves.some((l) => l.user_id === uid && l.start_date <= ds && l.end_date >= ds);
  const todayDs = fmtDate(new Date());
  const monShort = new Date(blk.y, blk.m, 1).toLocaleString("en-GB", { month: "short" });
  const rangeLabel = `${monShort} ${blk.bStart}–${blk.bEnd}, ${blk.y}`;
  const tNow = new Date();
  const notNow = !(blk.y === tNow.getFullYear() && blk.m === tNow.getMonth() && tNow.getDate() >= blk.bStart && tNow.getDate() <= blk.bEnd);
  const cellText = (u, d) => {
    if (onLeave(u.id, d.ds)) return "Leave";
    const c = resolveCellMonth(u.month_schedule, d.date, defaultStart);
    return c.kind === "off" ? "Off" : c.label;
  };
  const exportCsv = () => {
    const header = ["Staff", "Role", ...days.map((d) => `${d.wd} ${d.d}`)];
    const body = users.map((u) => [u.name, u.role, ...days.map((d) => cellText(u, d))]);
    const csv = [header, ...body].map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `team-week-${days[0].ds}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const printGrid = () => {
    const company = CONFIG.company?.name || "Rozfa Printing";
    const head = `<tr><th class="staff">Staff</th>${days.map((d) => `<th>${d.wd}<br><span class="dt">${d.d}</span></th>`).join("")}</tr>`;
    const bodyRows = users.map((u) => `<tr><td class="staff">${u.name}<br><span class="role">${u.role}</span></td>${days.map((d) => { const t = cellText(u, d); const cls = t === "Off" ? "off" : t === "Leave" ? "leave" : "work"; return `<td class="${cls}">${t}</td>`; }).join("")}</tr>`).join("");
    const html = `<!doctype html><html><head><title>Team Week</title><meta charset="utf-8"><style>
      *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
      body{padding:24px;color:#0f172a}
      h1{margin:0;font-size:22px}
      h2{margin:4px 0 16px;font-size:14px;color:#475569;font-weight:600}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:center}
      th{background:#0891b2;color:#fff;font-size:11px;text-transform:uppercase}
      th.staff,td.staff{text-align:left;white-space:nowrap}
      .dt{font-weight:normal;font-size:10px}
      .role{color:#64748b;font-size:10px;text-transform:uppercase}
      td.off{color:#94a3b8}
      td.leave{background:#fef3c7;color:#92400e;font-weight:600}
      td.work{background:#ecfeff;color:#155e75;font-weight:600}
      .foot{margin-top:14px;font-size:10px;color:#94a3b8}
      @media print{@page{size:landscape;margin:12mm}}
    </style></head><body>
      <h1>${company}</h1>
      <h2>Weekly Timetable &mdash; ${rangeLabel}</h2>
      <table><thead>${head}</thead><tbody>${bodyRows}</tbody></table>
      <p class="foot">Printed ${new Date().toLocaleString("en-GB")}</p>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <Card className="p-4 space-y-3" data-testid="team-week">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold flex items-center gap-2"><CalendarRange className="w-4 h-4" /> Team Week — everyone's schedule at a glance</div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setAnchor(new Date(blk.y, blk.m, blk.bStart - 1))} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-mono" data-testid="week-range">{rangeLabel}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setAnchor(new Date(blk.y, blk.m, blk.bEnd + 1))} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
          {notNow && <Button size="sm" variant="ghost" onClick={() => setAnchor(new Date())} data-testid="week-today">This week</Button>}
          <div className="w-px h-6 bg-border mx-1" />
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="week-export"><Download className="w-4 h-4 mr-1" /> Export</Button>
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={printGrid} data-testid="week-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase font-mono text-muted-foreground border-b">
              <th className="p-2 text-left">Staff</th>
              {days.map((d) => (
                <th key={d.d} className={`p-2 text-center ${d.ds === todayDs ? "text-cyan-600" : ""}`}>
                  <div>{d.wd}</div><div className="font-sans normal-case text-[10px]">{d.d}/{blk.m + 1}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="team-week-rows">
            {users.map((u) => (
              <tr key={u.id} className="border-b" data-testid={`week-row-${u.id}`}>
                <td className="p-2 font-medium whitespace-nowrap">{u.name}<div className="text-[10px] text-muted-foreground font-mono uppercase">{u.role}</div></td>
                {days.map((d) => {
                  const fast = isFast(u.id, d.ds);
                  const dot = fast ? <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500" title="Fasting day" data-testid={`week-fast-${u.id}-${d.ds}`} /> : null;
                  if (onLeave(u.id, d.ds)) return <td key={d.d} className="p-1 text-center relative">{dot}<Badge className="bg-amber-100 text-amber-800 font-normal hover:bg-amber-100">Leave</Badge></td>;
                  const c = resolveCellMonth(u.month_schedule, d.date, defaultStart);
                  if (c.kind === "off") return <td key={d.d} className="p-1 text-center relative">{dot}<Badge variant="outline" className="text-muted-foreground font-normal">Off</Badge></td>;
                  const sk = shiftKeyByLabel(c.label);
                  const cls = c.kind === "default" ? "text-muted-foreground border border-dashed border-border" : (SHIFT_COLORS[sk] || "bg-cyan-50 text-cyan-800 dark:bg-cyan-950/50 dark:text-cyan-300");
                  return <td key={d.d} className="p-1 text-center relative">{dot}<span className={`inline-block rounded px-2 py-1 text-xs font-mono ${cls}`}>{c.label}</span></td>;
                })}
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={days.length + 1} className="p-8 text-center text-muted-foreground">No staff.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-blue-100 border border-blue-300" /> 9–6</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-slate-200 border border-slate-400" /> 10–7</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-orange-100 border border-orange-300" /> 12–9</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-emerald-100 border border-emerald-300" /> 10–8</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded border-2 border-dashed border-muted-foreground/50" /> Default start (blank day)</span>
        <span className="flex items-center gap-1"><Badge variant="outline" className="h-4 px-1 text-[10px] font-normal">Off</Badge> Rest day</span>
        <span className="flex items-center gap-1"><Badge className="h-4 px-1 text-[10px] bg-amber-100 text-amber-800 font-normal hover:bg-amber-100">Leave</Badge> Approved leave</span>
        <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" /> Fasting day</span>
      </div>
    </Card>
  );
}

const LEAVE_COLORS = ["bg-sky-100 text-sky-800", "bg-violet-100 text-violet-800", "bg-emerald-100 text-emerald-800", "bg-rose-100 text-rose-800", "bg-amber-100 text-amber-800"];
const monthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

function LeaveCalendar() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState({ leaves: [], holidays: [] });
  const month = monthStr(cursor);
  useEffect(() => { api.get("/leave/calendar", { params: { month } }).then((r) => setData(r.data)).catch(() => {}); }, [month]);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const holMap = {}; data.holidays.forEach((h) => { holMap[h.date] = h.name; });
  const colorMap = {}; let ci = 0;
  const colorFor = (name) => { if (!(name in colorMap)) colorMap[name] = LEAVE_COLORS[ci++ % LEAVE_COLORS.length]; return colorMap[name]; };
  const leavesOn = (ds) => data.leaves.filter((l) => l.start_date <= ds && l.end_date >= ds);
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Card className="p-4" data-testid="leave-calendar">
      <div className="flex items-center justify-between mb-4">
        <Button size="icon" variant="outline" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-display font-bold text-lg flex items-center gap-2"><CalendarRange className="w-5 h-5" /> {cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
        <Button size="icon" variant="outline" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-mono text-muted-foreground mb-1">{weekdays.map((w) => <div key={w}>{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, idx) => {
          if (d === null) return <div key={`blank-${idx}`} />;
          const ds = `${month}-${String(d).padStart(2, "0")}`;
          const hol = holMap[ds]; const ppl = leavesOn(ds);
          return (
            <div key={ds} className={`min-h-[86px] rounded-md border p-1 text-left ${hol ? "bg-rose-50 dark:bg-rose-950/20 border-rose-200" : ""}`} data-testid={`cal-day-${ds}`}>
              <div className="text-xs font-semibold flex items-center justify-between">{d}{ppl.length > 0 && <span className="text-[10px] font-mono text-muted-foreground">{ppl.length}</span>}</div>
              {hol && <div className="text-[10px] text-rose-700 font-medium truncate" title={hol}>{hol}</div>}
              <div className="space-y-0.5 mt-0.5">
                {ppl.slice(0, 3).map((l) => <div key={l.id} className={`text-[10px] rounded px-1 truncate ${colorFor(l.type)}`} title={`${l.user_name} — ${l.type}`}>{l.user_name}</div>)}
                {ppl.length > 3 && <div className="text-[10px] text-muted-foreground">+{ppl.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function LeaveTypeCard({ user, b, onSaved }) {
  const [q, setQ] = useState(b.quota);
  const [form, setForm] = useState({ start_date: todayStr(), end_date: todayStr(), status: "approved", reason: "" });
  useEffect(() => setQ(b.quota), [b.quota]);
  const saveQuota = async () => { await api.patch(`/users/${user.id}`, { leave_quotas: { [b.type]: Number(q) } }); toast.success(`${b.type} entitlement saved`); onSaved && onSaved(); };
  const addLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leave", { ...form, type: b.type, user_id: user.id });
      toast.success(`${b.type} added`);
      setForm((f) => ({ ...f, reason: "" }));
      onSaved && onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const tid = b.type.replace(/\s+/g, "-").toLowerCase();
  return (
    <div className="border rounded-md p-3 space-y-2" data-testid={`leave-card-${tid}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold">{b.type}</div>
        <div className="flex items-center gap-1">
          <Label className="text-[11px] text-muted-foreground">Entitled / year</Label>
          <Input type="number" min="0" value={q} onChange={(e) => setQ(e.target.value)} className="w-16 h-7 text-right" data-testid={`leave-quota-input-${tid}`} />
          <Button size="sm" variant="outline" className="h-7" onClick={saveQuota} data-testid={`leave-quota-save-${tid}`}>Save</Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Used", b.used], ["Pending", b.pending], ["Left", b.limited ? b.remaining : "∞"]].map(([l, v]) => (
          <div key={l} className="rounded-md bg-muted/40 py-1"><div className="font-bold font-mono">{v}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
        ))}
      </div>
      <form onSubmit={addLeave} className="space-y-2 pt-1 border-t">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid={`leave-start-${tid}`} /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid={`leave-end-${tid}`} /></div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid={`leave-status-${tid}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="approved">Approved</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent></Select></div>
          <Input placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="flex-1" data-testid={`leave-reason-${tid}`} />
          <Button type="submit" size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid={`leave-add-${tid}`}>Add</Button>
        </div>
      </form>
    </div>
  );
}

function StaffManageDialog({ user, onClose }) {
  const [balTypes, setBalTypes] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [recs, setRecs] = useState([]);
  const reload = useCallback(() => {
    api.get("/leave/balance-types", { params: { user_id: user.id } }).then((r) => setBalTypes(r.data)).catch(() => {});
    api.get("/leave", { params: { user_id: user.id } }).then((r) => setLeaves(r.data)).catch(() => {});
    api.get("/attendance", { params: { user_id: user.id, start: ago(30), end: todayStr() } }).then((r) => setRecs(r.data)).catch(() => {});
  }, [user.id]);
  useEffect(() => { reload(); }, [reload]);

  const review = async (id, status) => { await api.patch(`/leave/${id}`, { status }); reload(); };
  const delLeave = async (id) => { await api.delete(`/leave/${id}`); reload(); };
  const resetFace = async () => { if (!window.confirm("Reset this staff's enrolled face?")) return; await api.delete(`/attendance/face/${user.id}`); toast.success("Face enrollment reset"); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="staff-manage-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="w-4 h-4" /> {user.name} <span className="text-xs text-muted-foreground">({user.role})</span></DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase text-muted-foreground">Leave entitlement & requests — one form per leave type</Label>
            <Button size="sm" variant="outline" className="text-rose-600" onClick={resetFace} data-testid="sa-reset-face"><ScanFace className="w-3.5 h-3.5 mr-1" /> Reset Face</Button>
          </div>
          <div className="space-y-3" data-testid="leave-cards">
            {balTypes.map((b) => <LeaveTypeCard key={b.type} user={user} b={b} onSaved={reload} />)}
            {balTypes.length === 0 && <div className="text-xs text-muted-foreground">No leave types configured. Add them in Attendance → Settings.</div>}
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Leave history</div>
            <div className="space-y-1">
              {leaves.length === 0 && <div className="text-xs text-muted-foreground">No leave records.</div>}
              {leaves.map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm border rounded-md px-2 py-1" data-testid={`sa-leave-${l.id}`}>
                  <span>{l.type} · <span className="font-mono text-xs">{formatDate(l.start_date)}–{formatDate(l.end_date)}</span> · {l.days}d <Badge className={LEAVE_CLS[l.status]}>{l.status}</Badge></span>
                  <span className="flex gap-1">
                    {l.status === "pending" && <>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-700" onClick={() => review(l.id, "approved")}><CheckCircle2 className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-700" onClick={() => review(l.id, "rejected")}><XCircle className="w-4 h-4" /></Button>
                    </>}
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600" onClick={() => delLeave(l.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Recent attendance (30 days)</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {recs.length === 0 && <div className="text-xs text-muted-foreground">No attendance records.</div>}
              {recs.map((r) => {
                const selfie = (r.sessions || [])[0]?.in?.selfie_file_id;
                return (
                <div key={r.id} className="flex items-center justify-between text-xs border rounded-md px-2 py-1">
                  <span className="flex items-center gap-2">
                    {selfie && <img src={`${API}/files/${selfie}/download`} alt="selfie" className="w-7 h-7 rounded object-cover" />}
                    {formatDate(r.date)}
                  </span>
                  <span className="font-mono">{t(r.first_in)} → {t(r.last_out)}</span>
                  <span>{mins(r.work_minutes)}{r.late && <Badge className="ml-1 bg-amber-500">LATE</Badge>}{r.fasting && <Badge className="ml-1 bg-violet-600" title={r.remark || "Fasting day"}><Moon className="w-3 h-3" /></Badge>}</span>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffAttendance() {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent")));
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="staff-attendance">
      <Input placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="sa-search" />
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground"><tr className="text-left"><th className="p-2">Staff</th><th className="p-2">Role</th><th className="p-2 text-right">Manage</th></tr></thead>
          <tbody data-testid="sa-rows">
            {filtered.map((u) => (
              <tr key={u.id} className="border-t hover:bg-muted/40" data-testid={`sa-row-${u.email}`}>
                <td className="p-2 font-medium">{u.name}</td>
                <td className="p-2 text-xs">{u.role}</td>
                <td className="p-2 text-right"><div className="flex gap-2 justify-end"><StaffEditDialog user={u} onSaved={load} showQuota={false} /><Button size="sm" variant="outline" onClick={() => setSel(u)} data-testid={`sa-manage-${u.email}`}>Manage</Button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="text-[11px] text-muted-foreground">Click <b>Manage</b> to set each staff's leave entitlement and add leave per type.</p>
      {sel && <StaffManageDialog user={sel} onClose={() => { setSel(null); load(); }} />}
    </div>
  );
}

function ExemptStaff() {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent"))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (u, val) => {
    try { await api.patch(`/users/${u.id}`, { attendance_exempt: val }); toast.success(`${u.name}: ${val ? "exempt from clock-in" : "clock-in required"}`); load(); }
    catch { toast.error("Failed to update"); }
  };
  return (
    <div className="border rounded-md p-3 space-y-2" data-testid="exempt-editor">
      <Label className="flex items-center gap-2"><UserX className="w-4 h-4" /> Clock-in exemption</Label>
      <p className="text-[11px] text-muted-foreground">Exempt staff don't need to clock in and are never counted late or absent. The Rozfa Admin is always exempt.</p>
      <div className="divide-y max-h-72 overflow-y-auto">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 py-1.5" data-testid={`exempt-row-${u.id}`}>
            <div className="min-w-0"><div className="text-sm font-medium truncate">{u.name}</div><div className="text-[10px] text-muted-foreground font-mono uppercase">{u.role}</div></div>
            <div className="ml-auto">
              {u.is_super ? <span className="text-xs text-muted-foreground">Always exempt</span>
                : <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={!!u.attendance_exempt} onCheckedChange={(v) => toggle(u, !!v)} data-testid={`exempt-toggle-${u.id}`} /> No clock-in</label>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AttSettings() {
  const { user: me } = useAuth();
  const [s, setS] = useState(null);
  const [newType, setNewType] = useState("");
  useEffect(() => { api.get("/attendance/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const addType = () => {
    const v = newType.trim();
    if (!v) return;
    if ((s.leave_types || []).some((x) => x.toLowerCase() === v.toLowerCase())) return toast.error("That leave type already exists");
    setS({ ...s, leave_types: [...(s.leave_types || []), v] });
    setNewType("");
  };
  const delType = (name) => {
    if (name === s.quota_leave_type) return toast.error("This type is set as 'Quota applies to'. Change that first, then remove it.");
    setS({ ...s, leave_types: (s.leave_types || []).filter((x) => x !== name) });
  };
  const setLoc = (i, k, v) => setS({ ...s, locations: s.locations.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) });
  const addLoc = () => setS({ ...s, locations: [...s.locations, { name: "", lat: "", lng: "", radius_m: 50 }] });
  const addMyIp = async () => { try { const r = await api.get("/attendance/my-ip"); setS({ ...s, allowed_ips: Array.from(new Set([...(s.allowed_ips || []), r.data.ip])) }); toast.success(`Added ${r.data.ip}`); } catch { toast.error("Failed to detect IP"); } };
  const delLoc = (i) => setS({ ...s, locations: s.locations.filter((_, idx) => idx !== i) });
  const applyMyLoc = async (i) => { const p = await getPos(); if (!p) return toast.error("GPS unavailable"); setS({ ...s, locations: s.locations.map((l, idx) => (idx === i ? { ...l, lat: p.lat.toFixed(6), lng: p.lng.toFixed(6) } : l)) }); };
  const save = async () => {
    const bw = s.best_weights || {};
    const sum = ["attendance", "punctuality", "full_hours", "break", "completeness"].reduce((a, k) => a + (Number(bw[k]) || 0), 0);
    if (sum !== 100) return toast.error(`KPI weights must total 100% (currently ${sum}%)`);
    try { const r = await api.put("/attendance/settings", { value: s }); setS(r.data); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="p-5 space-y-4 max-w-2xl" data-testid="att-settings-card">
      <div className="font-semibold flex items-center gap-2"><Settings2 className="w-4 h-4" /> Attendance Settings</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Default work start time</Label>
          <Input type="time" value={s.work_start} onChange={(e) => setS({ ...s, work_start: e.target.value })} data-testid="set-work-start" />
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {ROZFA_PRESETS.map((p) => (
              <Button key={p.label} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setS({ ...s, work_start: p.start })} data-testid={`set-preset-${p.start}`}>{p.label}</Button>
            ))}
          </div>
        </div>
        <div><Label>Late grace (minutes)</Label><Input type="number" min="0" value={s.late_grace_min} onChange={(e) => setS({ ...s, late_grace_min: e.target.value })} data-testid="set-grace" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Break length (minutes)</Label><Input type="number" min="0" value={s.break_minutes ?? 60} onChange={(e) => setS({ ...s, break_minutes: e.target.value })} data-testid="set-break-minutes" /><p className="text-[11px] text-muted-foreground mt-1">Used for the "Break punctuality" KPI — returning later than this counts against a staff.</p></div>
        <div><Label>Fasting early-leave allowance (minutes)</Label><Input type="number" min="0" value={s.fasting_early_min ?? 60} onChange={(e) => setS({ ...s, fasting_early_min: e.target.value })} data-testid="set-fasting-early-min" /><p className="text-[11px] text-muted-foreground mt-1">On a fasting day, staff may clock out this many minutes before shift end without being marked early.</p></div>
      </div>
      {(() => {
        const bw = s.best_weights || { attendance: 40, punctuality: 30, full_hours: 15, break: 10, completeness: 5 };
        const WK = [["attendance", "Attendance"], ["punctuality", "Punctuality"], ["full_hours", "Full Hours (no early leave)"], ["break", "Break punctuality"], ["completeness", "Completeness"]];
        const sum = WK.reduce((a, [k]) => a + (Number(bw[k]) || 0), 0);
        const setW = (k, v) => setS({ ...s, best_weights: { ...bw, [k]: v === "" ? "" : Math.max(0, parseInt(v) || 0) } });
        return (
          <div className="border rounded-md p-3 space-y-3" data-testid="best-scoring-editor">
            <Label className="flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Best Attendance scoring</Label>
            <p className="text-[11px] text-muted-foreground -mt-1">Set how much each KPI counts toward the monthly Best Attendance score. The five weights must total exactly 100%.</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {WK.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[11px]">{label}</Label>
                  <div className="relative"><Input type="number" min="0" max="100" value={bw[k] ?? 0} onChange={(e) => setW(k, e.target.value)} className="pr-6" data-testid={`set-weight-${k}`} /><span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span></div>
                </div>
              ))}
            </div>
            <div className={`text-xs font-semibold ${sum === 100 ? "text-emerald-600" : "text-rose-600"}`} data-testid="best-weight-total">Total: {sum}% {sum === 100 ? "✓" : "— must equal 100%"}</div>
            <div className="max-w-xs">
              <Label>Eligibility threshold (%)</Label>
              <div className="relative"><Input type="number" min="0" max="100" value={s.eligibility_pct ?? 50} onChange={(e) => setS({ ...s, eligibility_pct: e.target.value })} className="pr-6" data-testid="set-eligibility-pct" /><span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span></div>
              <p className="text-[11px] text-muted-foreground mt-1">Staff must be present on at least this share of their mandatory days to appear in the ranking.</p>
            </div>
          </div>
        );
      })()}
      <p className="text-xs text-muted-foreground -mt-1">Each staff's attendance follows their own Weekly Timetable. This default only applies to staff who have no timetable set.</p>
      <p className="text-xs text-muted-foreground">Set each staff's leave entitlement per type in Staff Admin → Manage. Requests above remaining balance are blocked.</p>
      <div className="border rounded-md p-3 space-y-2" data-testid="leave-types-editor">
        <Label className="flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Leave types</Label>
        <p className="text-[11px] text-muted-foreground">Add or remove the leave types staff can request — no code change needed.</p>
        <div className="flex flex-wrap gap-2">
          {(s.leave_types || []).map((x) => (
            <span key={x} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 pl-3 pr-1 py-1 text-sm" data-testid={`leave-type-${x}`}>
              {x}
              <button type="button" onClick={() => delType(x)} className="rounded-full p-0.5 hover:bg-rose-100 text-rose-600" data-testid={`leave-type-del-${x}`}><XCircle className="w-4 h-4" /></button>
            </span>
          ))}
          {(s.leave_types || []).length === 0 && <span className="text-sm text-muted-foreground">No leave types yet.</span>}
        </div>
        <div className="flex gap-2">
          <Input placeholder="e.g. Unpaid Leave" value={newType} onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addType(); } }} className="max-w-xs" data-testid="leave-type-input" />
          <Button type="button" size="sm" variant="outline" onClick={addType} data-testid="leave-type-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </div>
      {me?.is_super && <ExemptStaff />}
      <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2">
        <input type="checkbox" checked={s.require_geofence} onChange={(e) => setS({ ...s, require_geofence: e.target.checked })} data-testid="set-require-geofence" />
        <span><b>Enforce geo-fence</b> — block clock in/out when outside allowed locations (set radius per location below, e.g. 50 m)</span>
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2">
        <input type="checkbox" checked={!!s.require_selfie} onChange={(e) => setS({ ...s, require_selfie: e.target.checked })} data-testid="set-require-selfie" />
        <span><b>Require selfie</b> — staff must take a selfie (front camera) on every clock in/out</span>
      </label>
      <div className="border rounded-md p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!s.require_face_match} onChange={(e) => setS({ ...s, require_face_match: e.target.checked })} data-testid="set-require-face" />
          <span><b>AI Face Match</b> — verify the clock-in selfie against each staff's enrolled face photo to prevent buddy-punching (auto-requires selfie)</span>
        </label>
        {s.require_face_match && (
          <div className="flex items-center gap-2 pl-6">
            <Label className="text-xs">Match threshold</Label>
            <Input type="number" min="0" max="100" value={s.face_threshold} onChange={(e) => setS({ ...s, face_threshold: e.target.value })} className="w-20 h-8" data-testid="set-face-threshold" />
            <span className="text-xs text-muted-foreground">% confidence required to accept. Staff enroll their face in the Clock In/Out tab.</span>
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2">
        <input type="checkbox" checked={!!s.require_wifi} onChange={(e) => setS({ ...s, require_wifi: e.target.checked })} data-testid="set-require-wifi" />
        <span><b>Company WiFi/network only</b> — block clock in/out unless device is on an allowed IP (public IP of the office WiFi)</span>
      </label>
      {s.require_wifi && (
        <div className="border rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Allowed IPs / CIDR (one per line)</Label>
            <Button size="sm" variant="outline" onClick={addMyIp} data-testid="set-add-myip"><Wifi className="w-3.5 h-3.5 mr-1" /> Add my current IP</Button>
          </div>
          <Textarea rows={3} placeholder={"203.0.113.5\n203.0.113.0/24"} value={(s.allowed_ips || []).join("\n")} onChange={(e) => setS({ ...s, allowed_ips: e.target.value.split(/[\n,]/).map((x) => x.trim()).filter(Boolean) })} data-testid="set-allowed-ips" />
          <p className="text-[11px] text-muted-foreground">Browsers can't read WiFi names, so access is verified by the network's public IP. Connect to the office WiFi and click "Add my current IP".</p>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Allowed work locations</Label><Button size="sm" variant="outline" onClick={addLoc} data-testid="set-add-loc"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
        <div className="space-y-2">
          {s.locations.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2" data-testid={`loc-${i}`}>
              <div className="col-span-4"><Label className="text-xs">Name</Label><Input value={l.name} onChange={(e) => setLoc(i, "name", e.target.value)} data-testid={`loc-name-${i}`} /></div>
              <div className="col-span-3"><Label className="text-xs">Lat</Label><Input value={l.lat} onChange={(e) => setLoc(i, "lat", e.target.value)} data-testid={`loc-lat-${i}`} /></div>
              <div className="col-span-3"><Label className="text-xs">Lng</Label><Input value={l.lng} onChange={(e) => setLoc(i, "lng", e.target.value)} data-testid={`loc-lng-${i}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Radius m</Label><Input type="number" value={l.radius_m} onChange={(e) => setLoc(i, "radius_m", e.target.value)} data-testid={`loc-radius-${i}`} /></div>
              <div className="col-span-12 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => applyMyLoc(i)} data-testid={`loc-gps-${i}`}><MapPin className="w-3.5 h-3.5 mr-1" /> Use my GPS</Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => delLoc(i)} data-testid={`loc-del-${i}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button>
              </div>
            </div>
          ))}
          {s.locations.length === 0 && <div className="text-sm text-muted-foreground">No locations set. Geo-fence enforcement needs at least one location.</div>}
        </div>
      </div>
      <Button onClick={save} className="bg-cyan-600 hover:bg-cyan-700" data-testid="set-save">Save Settings</Button>
    </Card>
  );
}
